import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Events = () => {
  const events = [
    {
      title: 'AI Day @ GITAM University',
      date: '5 – 6 March 2026',
      location: 'Hyderabad, Telangana',
      description: 'A 2-day AI hackathon in collaboration with GITAM School of Computer Science & Engineering. 12 real-world use cases across Dentistry, Diabetes, Radiology, and Finance.',
      image: '/images/backgrounds/GITAM_Hyderabad_Campus.jpg',
      link: '/events/gitam-university',
      status: 'upcoming',
      tags: ['Hackathon', '2 Days', '12 Use Cases'],
    },
    {
      title: 'AI Day @ Anurag University',
      date: '5 January 2026',
      location: 'Hyderabad, Telangana',
      description: 'A flagship AI experience — showcasing real-world AI breakthroughs, hands-on workshops, project expo, research paper presentations, and industry insights.',
      image: '/images/events/Anurag-University-Hyderabad-Im.png',
      link: '/events/anurag-university',
      status: 'past',
      tags: ['Expo', 'Talks', 'Workshops'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>AI Day Events — Upcoming & Past AI Events by AI Anytime</title>
        <meta name="description" content="Browse all AI Day events — upcoming hackathons and past editions at universities across India. An initiative by AI Anytime & Kodryx AI." />
        <meta property="og:title" content="AI Day Events — Upcoming & Past AI Events" />
        <meta property="og:description" content="Browse all AI Day events — upcoming hackathons and past editions at universities across India." />
        <meta property="og:url" content="https://aidayevents.com/events" />
        <meta property="og:image" content="https://aidayevents.com/images/backgrounds/GITAM_Hyderabad_Campus.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Hero */}
      <section className="bg-black pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-outfit font-800 text-white mb-4">
            AI Day <span className="text-crimson">Events</span>
          </h1>
          <p className="text-lg font-outfit font-600 text-gray-400 max-w-2xl mx-auto">
            Explore all AI Day editions — past and upcoming. Click on any event to learn more.
          </p>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Upcoming Events */}
          <h2 className="text-2xl font-outfit font-800 text-black mb-8">
            Upcoming Events
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {events.filter(e => e.status === 'upcoming').map((event, index) => (
              <Link 
                key={index}
                to={event.link}
                className="group bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={event.image} 
                    alt={event.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4 bg-crimson px-3 py-1 rounded">
                    <span className="text-white font-outfit font-800 text-xs uppercase">Upcoming</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-outfit font-800 text-black mb-3 group-hover:text-crimson transition-colors">
                    {event.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-500 font-outfit font-600">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-crimson" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {event.date}
                    </div>
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-crimson" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {event.location}
                    </div>
                  </div>
                  <p className="text-gray-600 font-outfit font-600 text-sm mb-4">
                    {event.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {event.tags.map((tag, i) => (
                      <span key={i} className="bg-gray-100 text-gray-700 font-outfit font-600 text-xs px-3 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Past Events */}
          <h2 className="text-2xl font-outfit font-800 text-black mb-8">
            Past Events
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {events.filter(e => e.status === 'past').map((event, index) => (
              <Link 
                key={index}
                to={event.link}
                className="group bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={event.image} 
                    alt={event.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 grayscale group-hover:grayscale-0"
                  />
                  <div className="absolute top-4 left-4 bg-gray-700 px-3 py-1 rounded">
                    <span className="text-white font-outfit font-800 text-xs uppercase">Past Event</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-outfit font-800 text-black mb-3 group-hover:text-crimson transition-colors">
                    {event.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-500 font-outfit font-600">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {event.date}
                    </div>
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {event.location}
                    </div>
                  </div>
                  <p className="text-gray-600 font-outfit font-600 text-sm mb-4">
                    {event.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {event.tags.map((tag, i) => (
                      <span key={i} className="bg-gray-100 text-gray-700 font-outfit font-600 text-xs px-3 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
